import type {
  EditorSDK,
  PatternConfig,
  ReadWriteMode,
  RouterConfig,
} from '@wix/wix-data-client-common-standalone'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import {
  getDatasetConfig,
  getPatternDefForPage,
  setDatasetConfig,
  updatePatternDef,
} from '@wix/wix-data-client-common'

export type CombinedRouterDatasetConfig = {
  dataset: Omit<PatternConfig, 'seoV2' | 'private' | 'lowercase'> & {
    readWriteType: ReadWriteMode
    collectionName: string
  }
}

export const getRouterDatasetConfig = async function (
  editorSdkProxy: EditorSDK,
  controllerRef: ComponentRef,
  controllerConfig: CombinedRouterDatasetConfig | undefined,
) {
  const pageRef = await editorSdkProxy.components.getPage({
    componentRef: controllerRef,
  })
  const routerRef = await editorSdkProxy.routers.getByPage({ pageRef })
  const { config: routerConfig, pages: routerPages } =
    await editorSdkProxy.routers.get<RouterConfig>({ routerRef })
  const { def: patternDef } = getPatternDefForPage(
    routerConfig,
    routerPages,
    pageRef,
  )
  const datasetConfigFromRouter = getDatasetConfig(patternDef)
  return {
    dataset: {
      ...controllerConfig?.dataset,
      collectionName: datasetConfigFromRouter.collection,
      filter: datasetConfigFromRouter.filter,
      sort: datasetConfigFromRouter.sort,
      pageSize: datasetConfigFromRouter.pageSize,
      includes: datasetConfigFromRouter.includes,
      includeFieldGroups: datasetConfigFromRouter.includeFieldGroups,
      cursor: datasetConfigFromRouter.cursor,
      nested: datasetConfigFromRouter.nested,
    },
  }
}

export const saveRouterDatasetConfig = async function (
  editorSdkProxy: EditorSDK,
  controllerRef: ComponentRef,
  config: CombinedRouterDatasetConfig,
) {
  const pageRef = await editorSdkProxy.components.getPage({
    componentRef: controllerRef,
  })
  const routerRef = await editorSdkProxy.routers.getByPage({ pageRef })
  const currentRouterData = await editorSdkProxy.routers.get<RouterConfig>({
    routerRef,
  })
  const { pattern, def: currentPatternDef } = getPatternDefForPage(
    currentRouterData.config,
    currentRouterData.pages,
    pageRef,
  )

  const { dataset } = config

  const routerDatasetConfig: PatternConfig = {
    collection: dataset.collectionName,
    filter: dataset.filter,
    sort: dataset.sort,
    pageSize: dataset.pageSize,
    includes: dataset.includes,
    includeFieldGroups: dataset.includeFieldGroups,
    lowercase: currentPatternDef.config.lowercase,
    cursor: dataset.cursor,
    nested: dataset.nested,
  }

  const newPatternDef = setDatasetConfig(currentPatternDef, routerDatasetConfig)

  const newRouterConfig = updatePatternDef(
    currentRouterData.config,
    pattern,
    newPatternDef,
  )
  const newControllerConfig = {
    dataset: {
      readWriteType: dataset.readWriteType,
    },
  }

  await Promise.all([
    editorSdkProxy.routers.update({ routerRef, config: newRouterConfig }),
    editorSdkProxy.controllers.saveConfiguration({
      controllerRef,
      config: newControllerConfig,
    }),
  ])
}
